import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  TextField, Button, IconButton, Alert, CircularProgress, Chip,
} from '@mui/material';
import { Close, DeleteForever, WarningAmber } from '@mui/icons-material';
import { apiFetch } from '../../utils/api';

const C = {
  card: '#0E1831', cardLight: '#121E38', border: '#1E2D55',
  text: '#F9F9F9', textMuted: '#878AA2', textSecondary: '#8384A5',
  success: '#0ECC68', error: '#E01B4F', warning: '#FFC23F', inputBg: '#10182E',
};

export interface DeleteTarget {
  id: string;
  name: string;
  email?: string;
}

interface Props {
  target: DeleteTarget | null;
  onClose: () => void;
  /** Fired after a successful delete so the caller can refresh its list. */
  onDeleted: (target: DeleteTarget, totalRows: number) => void;
}

const DeleteUserDialog: React.FC<Props> = ({ target, onClose, onDeleted }) => {
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setConfirm(''); setError(null); }, [target]);

  const matches = !!target && confirm.trim() === target.id;

  const run = async () => {
    if (!target || !matches) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<{ status: string; totalRows: number }>(
        `/api/admin/direct-users/${target.id}`,
        { method: 'DELETE', body: JSON.stringify({ confirm: target.id }) }
      );
      onDeleted(target, res.totalRows ?? 0);
      onClose();
    } catch (e: any) {
      let msg = e?.message || 'Delete failed';
      try { msg = JSON.parse(msg).message || msg; } catch { /* plain-text error */ }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={target !== null}
      onClose={() => { if (!busy) onClose(); }}
      maxWidth="xs" fullWidth
      PaperProps={{ sx: { bgcolor: C.card, border: `1px solid ${C.error}55`, borderRadius: 2, backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border}`, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: `${C.error}22`, color: C.error, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DeleteForever sx={{ fontSize: 19 }} />
          </Box>
          <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1rem' }}>Delete client permanently</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={busy} sx={{ color: C.textSecondary }}><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}
            sx={{ mb: 2, bgcolor: `${C.error}18`, color: C.error, border: `1px solid ${C.error}55` }}>
            {error}
          </Alert>
        )}

        <Box sx={{ bgcolor: C.cardLight, border: `1px solid ${C.border}`, borderRadius: 2, p: 2, mb: 2 }}>
          <Typography sx={{ color: C.text, fontSize: '0.95rem', fontWeight: 700 }}>{target?.name}</Typography>
          {target?.email && <Typography sx={{ color: C.textSecondary, fontSize: '0.78rem' }}>{target.email}</Typography>}
          <Chip label={`UID ${target?.id}`} size="small"
            sx={{ mt: 1, bgcolor: 'rgba(43,110,245,0.12)', color: '#5581F7', fontFamily: 'monospace', fontWeight: 600, height: 22 }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mb: 2 }}>
          <WarningAmber sx={{ color: C.warning, fontSize: 18, mt: '2px', flexShrink: 0 }} />
          <Typography sx={{ color: C.textMuted, fontSize: '0.8rem', lineHeight: 1.55 }}>
            This erases the account and every row attached to it — balance, wallet history,
            bets, bonuses, deposits, withdrawals, login history and referrals.
            <b style={{ color: C.text }}> It cannot be undone.</b>
          </Typography>
        </Box>

        <Typography sx={{ color: C.textSecondary, fontSize: '0.78rem', mb: 0.75 }}>
          Type <b style={{ color: C.text, fontFamily: 'monospace' }}>{target?.id}</b> to confirm
        </Typography>
        <TextField
          fullWidth size="small" autoFocus value={confirm}
          onChange={e => setConfirm(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && matches && !busy) run(); }}
          placeholder="Enter the UID"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: C.inputBg, borderRadius: 1.5,
              '& fieldset': { borderColor: matches ? C.success : C.border },
              '&:hover fieldset': { borderColor: matches ? C.success : C.error },
              '&.Mui-focused fieldset': { borderColor: matches ? C.success : C.error },
            },
            '& input': { color: C.text, fontFamily: 'monospace', fontSize: '0.9rem' },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${C.border}`, px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: C.textSecondary, textTransform: 'none' }}>Cancel</Button>
        <Button
          onClick={run} disabled={!matches || busy} variant="contained"
          startIcon={busy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DeleteForever />}
          sx={{
            bgcolor: C.error, textTransform: 'none', fontWeight: 600,
            '&:hover': { bgcolor: '#c01640' },
            '&.Mui-disabled': { bgcolor: C.cardLight, color: C.textSecondary },
          }}
        >
          {busy ? 'Deleting…' : 'Delete permanently'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteUserDialog;
